import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import type { Locale } from 'date-fns';

export interface LocalDateRange {
  startDate: string;
  endDate:   string;
  nights:    number;
}

interface ParseOptions {
  /** date-fns locale, for month names. */
  locale: Locale;
  /** `12/08` reads as 12 August everywhere except English, where it's 8 December. */
  dayFirst: boolean;
  today?: Date;
}

const MS_PER_DAY = 86_400_000;
const MAX_NIGHTS = 365;
const MAX_WRAPPED_NIGHTS = 90;

function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Month names (full + abbreviated, standalone + formatting forms) indexed 0-11.
 * Derived from date-fns instead of a hardcoded table so the parser covers every
 * language the app ships. English is always merged in — people type "August"
 * with the UI in Spanish often enough to be worth the two extra entries.
 */
function monthIndexByName(locale: Locale): Map<string, number> {
  const map = new Map<string, number>();
  for (const loc of [locale, enUS]) {
    for (let month = 0; month < 12; month++) {
      const sample = new Date(2000, month, 1);
      for (const pattern of ['LLLL', 'LLL', 'MMMM', 'MMM']) {
        const name = normalize(format(sample, pattern, { locale: loc })).replace(/\.$/, '');
        if (name && !/^\d+$/.test(name)) map.set(name, month);
      }
    }
  }
  return map;
}

/**
 * A written month, matched by prefix. date-fns only hands us two fixed
 * abbreviations per language, but people truncate wherever they please
 * ("sept", "sep", "agost", "febr"), and an unrecognised month used to be
 * silently inherited from the other end of the range — "del 25 sept al 02 ago"
 * read as 25 *August*. Ambiguous prefixes resolve to nothing rather than to a
 * guess.
 */
function resolveMonth(word: string, months: Map<string, number>): number | undefined {
  const exact = months.get(word);
  if (exact !== undefined) return exact;

  let found: number | undefined;
  for (const [name, index] of months) {
    if (!name.startsWith(word)) continue;
    if (found !== undefined && found !== index) return undefined;
    found = index;
  }
  return found;
}

/**
 * Words allowed to sit between the two dates without making the text
 * un-understood. Anything else and we hand over to the AI parser rather than
 * read a range out of fragments we only half recognise.
 */
const CONNECTOR_WORDS = new Set([
  'from', 'until', 'till', 'and', 'through', 'thru',
  'del', 'des', 'desde', 'hasta', 'entre', 'fins',
  'dal', 'dall', 'dallo', 'fino',
  'jusqu', 'jusqua', 'aux',
]);

interface DateToken {
  day:    number;
  month?: number;
  year?:  number;
}

interface Candidate {
  index:    number;
  length:   number;
  priority: number;
  token:    DateToken;
}

type Span = Pick<Candidate, 'index' | 'length'>;

/** True if any word outside the matched dates is one we did not understand. */
function hasUnreadWords(text: string, spans: Span[]): boolean {
  let masked = text;
  for (const span of spans) {
    masked = masked.slice(0, span.index) + ' '.repeat(span.length) + masked.slice(span.index + span.length);
  }
  for (const match of masked.matchAll(/[a-z]{3,}/g)) {
    if (!CONNECTOR_WORDS.has(match[0])) return true;
  }
  return false;
}

function fullYear(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  return raw.length === 2 ? 2000 + Number(raw) : Number(raw);
}

function numericToken(a: number, b: number, rawYear: string | undefined, dayFirst: boolean): DateToken | null {
  let day = dayFirst ? a : b;
  let month = dayFirst ? b : a;
  // A number above 12 can only be a day, whatever the locale convention says.
  if (a > 12 && b <= 12) { day = a; month = b; }
  else if (b > 12 && a <= 12) { day = b; month = a; }
  if (month < 1 || month > 12) return null;
  return { day, month: month - 1, year: fullYear(rawYear) };
}

/**
 * Every date-shaped run in the text, left to right, non-overlapping. Patterns
 * are tried most-specific first, so "10 de agosto" is one token rather than a
 * bare "10" plus leftovers.
 */
function collectTokens(text: string, months: Map<string, number>, dayFirst: boolean): Candidate[] {
  const patterns: { re: RegExp; build: (m: RegExpExecArray) => DateToken | null }[] = [
    { // 2026-08-03
      re: /(\d{4})-(\d{1,2})-(\d{1,2})(?!\d)/g,
      build: m => ({ year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) }),
    },
    { // 12/08/2026 · 12.08 · 8/12
      re: /(\d{1,2})(?!\d)[/.](\d{1,2})(?!\d)(?:[/.](\d{2,4})(?!\d))?/g,
      build: m => numericToken(Number(m[1]), Number(m[2]), m[3], dayFirst),
    },
    { // 3 de agosto de 2026 · 3 ago · 3 august 2026
      re: /(\d{1,2})(?!\d)\s*(?:de\s+|of\s+|d')?([a-z]{3,})\.?(?:\s+(?:del?\s+)?(\d{4})(?!\d))?/g,
      build: m => {
        const month = resolveMonth(m[2], months);
        return month === undefined ? null : { day: Number(m[1]), month, year: fullYear(m[3]) };
      },
    },
    { // august 3, 2026 · ago 3
      re: /([a-z]{3,})\.?\s+(\d{1,2})(?!\d)(?:\s*,?\s*(\d{4})(?!\d))?/g,
      build: m => {
        const month = resolveMonth(m[1], months);
        return month === undefined ? null : { day: Number(m[2]), month, year: fullYear(m[3]) };
      },
    },
    { // a lone day, as in "del 3 al 10 de agosto" — the month comes from its sibling
      re: /(\d{1,2})(?!\d)(?:st|nd|rd|th)?/g,
      build: m => ({ day: Number(m[1]) }),
    },
  ];

  const candidates: Candidate[] = [];
  patterns.forEach((pattern, priority) => {
    pattern.re.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.re.exec(text)) !== null) {
      const token = pattern.build(match);
      if (token) candidates.push({ index: match.index, length: match[0].length, priority, token });
    }
  });

  candidates.sort((a, b) => a.index - b.index || a.priority - b.priority || b.length - a.length);

  const picked: Candidate[] = [];
  let consumedUntil = -1;
  for (const candidate of candidates) {
    if (candidate.index < consumedUntil) continue;
    picked.push(candidate);
    consumedUntil = candidate.index + candidate.length;
  }
  return picked;
}

function makeDate(year: number, month: number, day: number): Date | null {
  if (month < 0 || month > 11 || day < 1 || day > 31) return null;
  const date = new Date(year, month, day);
  const valid = date.getFullYear() === year && date.getMonth() === month && date.getDate() === day;
  return valid ? date : null;
}

function toIso(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function resolveRange(start: DateToken, end: DateToken, today: Date): LocalDateRange | null {
  const startMonth = start.month ?? end.month;
  const endMonth   = end.month ?? start.month;
  if (startMonth === undefined || endMonth === undefined) return null;

  const statedYear = start.year ?? end.year;
  let startYear = start.year ?? statedYear ?? today.getFullYear();
  let endYear   = end.year   ?? statedYear ?? startYear;

  let from = makeDate(startYear, startMonth, start.day);
  let to   = makeDate(endYear, endMonth, end.day);
  if (!from || !to) return null;

  // "28 dic – 4 ene" spans the new year.
  let wrapped = false;
  if (to <= from) {
    wrapped = true;
    endYear += 1;
    to = makeDate(endYear, endMonth, end.day);
    if (!to) return null;
  }

  // No year anywhere → next occurrence on or after today, the same rule the AI
  // fallback follows, so both paths agree on what "3 al 10 de agosto" means.
  if (statedYear === undefined) {
    const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (to < midnight) {
      from = makeDate(startYear + 1, startMonth, start.day);
      to   = makeDate(endYear + 1, endMonth, end.day);
      if (!from || !to) return null;
    }
  }

  const nights = Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
  if (nights <= 0 || nights > MAX_NIGHTS) return null;
  // Rolling the end into the next year is a guess. It is a good one for
  // "28 dic – 4 ene" and a terrible one for "25 sept al 02 ago", which is far
  // more likely a typo than an eleven-month trip — hand those to the AI.
  if (wrapped && nights > MAX_WRAPPED_NIGHTS) return null;

  return { startDate: toIso(from), endDate: toIso(to), nights };
}

/**
 * Best-effort local reading of a free-text date range, so the common shapes
 * ("del 3 al 10 de agosto", "12/08 - 19/08", "Aug 3 to Aug 10") resolve
 * instantly in the browser instead of costing a round-trip to the AI parser.
 * Returns null for anything vague ("la primera semana de septiembre") or
 * ambiguous — the caller falls back to `tripsApi.parseDates` for those.
 */
export function parseLocalDateRange(text: string, opts: ParseOptions): LocalDateRange | null {
  const normalized = normalize(text.trim());
  if (!normalized) return null;

  const picked = collectTokens(normalized, monthIndexByName(opts.locale), opts.dayFirst);
  // Exactly two: fewer means it isn't a range, more means the text carries
  // numbers we can't attribute ("2 personas del 3 al 10") and guessing would
  // be worse than letting the AI read it.
  if (picked.length !== 2) return null;
  if (hasUnreadWords(normalized, picked)) return null;

  return resolveRange(picked[0].token, picked[1].token, opts.today ?? new Date());
}
